import React, { useEffect, useState } from "react";
import { Users, Briefcase, Inbox, Mail, LogOut } from "lucide-react";
import useSEO from "../hooks/useSEO";
import { admin, getToken, setToken } from "../lib/adminApi";
import { BRAND } from "../data/site";
import AdminLogin from "../components/admin/AdminLogin";
import CandidatesPanel from "../components/admin/CandidatesPanel";
import OpeningsPanel from "../components/admin/OpeningsPanel";
import EnquiriesPanel from "../components/admin/EnquiriesPanel";
import EmailsPanel from "../components/admin/EmailsPanel";

const TABS = [
  { id: "candidates", label: "Candidates", icon: Users, Panel: CandidatesPanel },
  { id: "openings", label: "Openings", icon: Briefcase, Panel: OpeningsPanel },
  { id: "enquiries", label: "Enquiries", icon: Inbox, Panel: EnquiriesPanel },
  { id: "emails", label: "Emails", icon: Mail, Panel: EmailsPanel },
];

export default function Admin() {
  useSEO({ title: "Talent Inbox · ATS IT Solutions Admin", description: "Restricted admin area." });
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!getToken());
  const [tab, setTab] = useState("candidates");
  const [summary, setSummary] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);

  useEffect(() => {
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
    meta.content = "noindex, nofollow";
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!getToken()) return;
    admin.get("/auth/me").then((r) => setUser(r.data)).catch(() => setToken(null)).finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    admin.get("/admin/summary").then((r) => setSummary(r.data)).catch(() => {});
    admin.get("/admin/email-status").then((r) => setEmailStatus(r.data)).catch(() => {});
  }, [user, tab]);

  const logout = () => { setToken(null); setUser(null); };

  if (checking) return <div className="min-h-screen bg-navy-deep grid place-items-center text-slate-500 text-sm">Checking session…</div>;
  if (!user) return <AdminLogin onSuccess={setUser} />;

  const Active = TABS.find((t) => t.id === tab).Panel;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-navy-deep">
      <header className="sticky top-0 z-40 bg-navy-deep/85 backdrop-blur-xl border-b border-white/10">
        <div className="container-x h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/assets/ats-logo.png" alt={`${BRAND.name} logo`} className="h-10 w-10 rounded-full shrink-0" />
            <div className="min-w-0"><p className="font-head text-white font-semibold text-sm leading-tight">Talent Inbox</p><p className="font-mono text-[10px] tracking-wider uppercase text-slate-500 truncate">{user.email}</p></div>
          </div>
          <nav className="hidden md:flex items-center gap-1" aria-label="Admin sections">
            {TABS.map((t) => (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} aria-current={tab === t.id} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${tab === t.id ? "bg-brand-orange text-white" : "text-slate-300 hover:text-white hover:bg-white/5"}`}>
                <t.icon size={15} /> {t.label}
              </button>
            ))}
          </nav>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"><LogOut size={15} /> <span className="hidden sm:inline">Sign out</span></button>
        </div>
        <nav className="md:hidden container-x pb-3 flex gap-2 overflow-x-auto" aria-label="Admin sections">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-mobile-${t.id}`} className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border ${tab === t.id ? "bg-brand-orange text-white border-brand-orange" : "text-slate-300 border-white/10"}`}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="container-x py-8 md:py-10">
        {emailStatus && (
          <div data-testid="admin-email-status" className={`mb-6 rounded-2xl border px-5 py-4 text-sm ${emailStatus.verified ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-amber-500/30 bg-amber-500/10 text-amber-100"}`}>
            <p className="font-medium">
              Email sender: {emailStatus.verified ? "Resend · " : "TEMPORARY platform sender · "}<span className="font-mono text-xs">{emailStatus.from}</span>
              <span className="ml-2 font-mono text-[10px] uppercase tracking-wider opacity-70">domain {emailStatus.status}</span>
            </p>
            {!emailStatus.verified && emailStatus.records?.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs opacity-80">DNS records still required for atmasammantech.com (auto-switches once verified)</summary>
                <ul className="mt-2 space-y-1 font-mono text-[11px] break-all">
                  {emailStatus.records.map((r, i) => <li key={i}>{r.type} · {r.name} · {r.value}{r.priority ? ` · priority ${r.priority}` : ""} · <span className="uppercase">{r.status}</span></li>)}
                </ul>
              </details>
            )}
          </div>
        )}
        {summary && (
          <div data-testid="admin-summary" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {[["Candidates", summary.applications], ["New profiles", summary.new_applications], ["Active openings", summary.active_openings], ["Enquiries", summary.contacts]].map(([k, v]) => (
              <div key={k} className="glass rounded-2xl p-4"><p className="font-mono text-[10px] tracking-wider uppercase text-slate-500">{k}</p><p className="font-head text-2xl text-white font-semibold mt-1">{v}</p></div>
            ))}
          </div>
        )}
        <Active />
      </main>
    </div>
  );
}
